/**
 * UPI Payment Interface Component
 * Handles UPI payments with QR code and UPI ID options
 */

import React, { useState, useEffect } from 'react';
import { Button } from './ui';

interface UPIPaymentInterfaceProps {
  orderId: string;
  amount: number;
  qrCode?: string;
  upiLink?: string;
  onPaymentComplete: (success: boolean, error?: string) => void;
  onCancel: () => void;
  className?: string;
}

export const UPIPaymentInterface: React.FC<UPIPaymentInterfaceProps> = ({
  orderId,
  amount,
  qrCode,
  upiLink,
  onPaymentComplete,
  onCancel,
  className = ''
}) => {
  const [paymentMethod, setPaymentMethod] = useState<'qr' | 'upi_id'>('qr');
  const [upiId, setUpiId] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(300); // 5 minutes
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'checking' | 'success' | 'failed'>('pending');

  useEffect(() => {
    // Start countdown timer
    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          onPaymentComplete(false, 'Payment timeout');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Start polling for payment status
    const statusTimer = setInterval(() => {
      checkPaymentStatus();
    }, 3000);

    return () => {
      clearInterval(timer);
      clearInterval(statusTimer);
    };
  }, [orderId]);

  const checkPaymentStatus = async () => {
    try {
      const response = await fetch(`/api/payments/status/${orderId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          const status = data.data.status;
          
          if (status === 'SUCCESS') {
            setPaymentStatus('success');
            onPaymentComplete(true);
          } else if (status === 'FAILED') {
            setPaymentStatus('failed');
            onPaymentComplete(false, 'Payment failed');
          }
        }
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
  };

  const handleUPIAppPayment = (appName: string) => {
    if (upiLink) {
      // Modify UPI link for specific app
      let modifiedLink = upiLink;
      
      switch (appName) {
        case 'phonepe':
          modifiedLink = upiLink.replace('upi://pay', 'phonepe://pay');
          break;
        case 'googlepay':
          modifiedLink = upiLink.replace('upi://pay', 'tez://upi/pay');
          break;
        case 'paytm':
          modifiedLink = upiLink.replace('upi://pay', 'paytmmp://pay');
          break;
      }
      
      window.location.href = modifiedLink;
      setPaymentStatus('checking');
    }
  };

  const handleUPIIdPayment = () => {
    if (!upiId.trim()) {
      alert('Please enter a valid UPI ID');
      return;
    }

    setIsProcessing(true);
    setPaymentStatus('checking');
    
    // Simulate UPI ID payment processing
    setTimeout(() => {
      setIsProcessing(false);
      // In real implementation, this would trigger the actual UPI payment
      alert(`Payment request sent to ${upiId}. Please complete the payment in your UPI app.`);
    }, 2000);
  };

  const copyUPILink = () => {
    if (upiLink) {
      navigator.clipboard.writeText(upiLink);
      alert('UPI link copied to clipboard');
    }
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  if (paymentStatus === 'success') {
    return (
      <div className={`bg-white rounded-lg shadow p-6 text-center ${className}`}>
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Payment Successful!</h3>
        <p className="text-gray-600 mb-4">Your account has been credited with ₹{amount.toLocaleString()}</p>
        <Button onClick={() => onPaymentComplete(true)}>Continue</Button>
      </div>
    );
  }

  if (paymentStatus === 'failed') {
    return (
      <div className={`bg-white rounded-lg shadow p-6 text-center ${className}`}>
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Payment Failed</h3>
        <p className="text-gray-600 mb-4">There was an issue processing your payment. Please try again.</p>
        <div className="flex gap-3 justify-center">
          <Button variant="outline" onClick={onCancel}>Try Again</Button>
          <Button onClick={() => window.location.reload()}>Retry Payment</Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow ${className}`}>
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">UPI Payment</h3>
            <p className="text-sm text-gray-600">Amount: ₹{amount.toLocaleString()}</p>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-500">Time remaining</div>
            <div className={`text-lg font-mono ${timeRemaining < 60 ? 'text-red-600' : 'text-gray-900'}`}>
              {formatTime(timeRemaining)}
            </div>
          </div>
        </div>
      </div>

      {/* Payment Method Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex -mb-px">
          <button
            onClick={() => setPaymentMethod('qr')}
            className={`px-6 py-3 text-sm font-medium border-b-2 ${
              paymentMethod === 'qr'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            QR Code
          </button>
          <button
            onClick={() => setPaymentMethod('upi_id')}
            className={`px-6 py-3 text-sm font-medium border-b-2 ${
              paymentMethod === 'upi_id'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            UPI ID
          </button>
        </nav>
      </div>

      {/* QR Code Payment */}
      {paymentMethod === 'qr' && (
        <div className="p-6">
          <div className="text-center">
            <h4 className="text-lg font-medium text-gray-900 mb-4">Scan QR Code</h4>
            
            {qrCode ? (
              <div className="mb-6">
                <img
                  src={qrCode}
                  alt="UPI QR Code"
                  className="w-48 h-48 mx-auto border border-gray-200 rounded-lg"
                />
                <p className="text-sm text-gray-600 mt-2">
                  Scan this QR code with any UPI app to pay
                </p>
              </div>
            ) : (
              <div className="w-48 h-48 mx-auto border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center mb-6">
                <div className="text-center">
                  <svg className="w-12 h-12 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <p className="text-sm text-gray-500">QR Code Loading...</p>
                </div>
              </div>
            )}

            {/* UPI App Buttons */}
            <div className="grid grid-cols-3 gap-3 mb-6">
              <button
                onClick={() => handleUPIAppPayment('phonepe')}
                className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="text-center">
                  <div className="w-8 h-8 bg-purple-600 rounded mx-auto mb-1"></div>
                  <div className="text-xs text-gray-700">PhonePe</div>
                </div>
              </button>
              
              <button
                onClick={() => handleUPIAppPayment('googlepay')}
                className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="text-center">
                  <div className="w-8 h-8 bg-blue-600 rounded mx-auto mb-1"></div>
                  <div className="text-xs text-gray-700">Google Pay</div>
                </div>
              </button>
              
              <button
                onClick={() => handleUPIAppPayment('paytm')}
                className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="text-center">
                  <div className="w-8 h-8 bg-blue-800 rounded mx-auto mb-1"></div>
                  <div className="text-xs text-gray-700">Paytm</div>
                </div>
              </button>
            </div>

            {upiLink && (
              <div className="mb-4">
                <button
                  onClick={copyUPILink}
                  className="text-sm text-blue-600 hover:text-blue-800 underline"
                >
                  Copy UPI Link
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* UPI ID Payment */}
      {paymentMethod === 'upi_id' && (
        <div className="p-6">
          <h4 className="text-lg font-medium text-gray-900 mb-4">Enter UPI ID</h4>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              UPI ID
            </label>
            <input
              type="text"
              value={upiId}
              onChange={(e) => setUpiId(e.target.value)}
              placeholder="example@paytm"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Enter your UPI ID (e.g., yourname@paytm, yourname@phonepe)
            </p>
          </div>

          <Button
            onClick={handleUPIIdPayment}
            disabled={!upiId.trim() || isProcessing}
            isLoading={isProcessing}
            className="w-full"
          >
            {isProcessing ? 'Processing...' : 'Pay with UPI ID'}
          </Button>
        </div>
      )}

      {/* Status Message */}
      {paymentStatus === 'checking' && (
        <div className="p-4 bg-blue-50 border-t border-blue-200">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
            <p className="text-sm text-blue-800">
              Waiting for payment confirmation...
            </p>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="p-6 border-t border-gray-200 bg-gray-50">
        <div className="flex justify-between items-center">
          <p className="text-sm text-gray-600">
            Order ID: {orderId}
          </p>
          <Button variant="outline" onClick={onCancel}>
            Cancel Payment
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UPIPaymentInterface;